import { c as _c } from "react/compiler-runtime";
// types
import { Column, ColumnDef, ColumnMeta, Row, Table } from "@tanstack/react-table";
import { StatusIntents } from "@repo/common/types";

// components
import { HeaderCell } from "@/components/table/header-cell";
import * as Text from "@/components/ui/text";
import { StatusChip } from "@/components/ui/status-chip";
import { Button } from "@/components/ui/button";
import { EstimateTablePrice } from "../estimate/estimate-table-price";

// utils
import { formatDate } from "@repo/common/utils/format-date";
import _get from "lodash/get";
import { cn } from "@repo/common/utils/cn";
import { InvoicesItem } from "../invoice/types";
import { EstimatesItem } from "../estimate/types";
import { CustomerEvent } from "@/hooks/use-customer-events";
type ColumnOptions<T> = {
  key: keyof T | string;
  header: string | ((props: {
    column: Column<T>;
    table: Table<T>;
  }) => React.ReactNode);
  accessorFn?: (row: T) => any;
  cell?: (value: any, row: Row<T>, table: Table<T>) => React.ReactNode;
  sortable?: boolean;
};
export function generateColumn<T>(options: ColumnOptions<T> & {
  meta?: ColumnMeta<T, {
    headerClassName?: string;
    cellClassName?: string;
  }>;
}): ColumnDef<T> {
  return {
    accessorKey: options.key,
    accessorFn: options.accessorFn,
    // makes sure the status column is an exact match
    filterFn: (row, columnId, filterValue) => String(row.getValue(columnId)).toLowerCase() === String(filterValue).toLowerCase(),
    header: ({
      column,
      table
    }) => {
      if (typeof options.header === "function") {
        return options.header({
          column,
          table
        });
      }
      return <HeaderCell<T> column={column} sortable={options.sortable ?? true}>
          {options.header}
        </HeaderCell>;
    },
    cell: options.cell ? ({
      row,
      table
    }) => options.cell!(row.getValue(options.key as string), row, table) : ({
      row
    }) => {
      const value = _get(row.original, options.key as string);
      return <Text.Body size="md">{value}</Text.Body>;
    },
    meta: options.meta
  };
}
export function generateDateColumn<T>({
  key,
  header,
  meta,
  timezone,
  fallbackToUTC = true
}: {
  key: keyof T;
  header: string;
  meta?: ColumnMeta<T, {
    headerClassName?: string;
    cellClassName?: string;
  }>;
  timezone?: string;
  fallbackToUTC?: boolean;
}): ColumnDef<T> {
  return generateColumn<T>({
    key,
    header,
    cell: value => <Text.Body size="md">
        {formatDate({
        date: value,
        timezone: timezone ?? (fallbackToUTC ? "UTC" : undefined)
      })}
      </Text.Body>,
    meta
  });
}
export function generateStatusColumn<T extends EstimatesItem | InvoicesItem | CustomerEvent>({
  key,
  header,
  statusMap,
  borderless = false,
  meta
}: {
  key: keyof T;
  header: string;
  statusMap: Record<string, {
    text: string;
    intent: StatusIntents;
  }>;
  borderless?: boolean;
  meta?: ColumnMeta<T, {
    headerClassName?: string;
    cellClassName?: string;
  }>;
}): ColumnDef<T> {
  return generateColumn<T>({
    key,
    header,
    cell: (value, row) => {
      const status = statusMap[value as string] || {
        text: "Unknown",
        intent: "neutral"
      };
      if (value === "PAST_DUE") {
        const daysPastDue = (row.original as InvoicesItem).daysPastDue && (row.original as InvoicesItem).daysPastDue > 0 ? (row.original as InvoicesItem).daysPastDue : null;
        if (daysPastDue) {
          return <StatusChip intent="danger" borderless={borderless}>
              {daysPastDue} days overdue
            </StatusChip>;
        }
      }
      return <StatusChip intent={status?.intent ?? "neutral"} borderless={borderless}>
          {status?.text ?? "-"}
        </StatusChip>;
    },
    meta
  });
}
export function generateActionColumn<T>(header: string, buttonText: string, onClick: (row: T) => void): ColumnDef<T> {
  return generateColumn<T>({
    key: "action",
    header,
    sortable: false,
    cell: (_, row) => <Button variant="fill" intent="action" size="sm" onClick={() => onClick(row.original)}>
        {buttonText}
      </Button>
  });
}
export function generateCurrencyColumn<T>({
  key,
  header,
  cell
}: {
  key: keyof T;
  header: string;
  cell?: (value: any, row: Row<T>, table: Table<T>) => React.ReactNode;
}): ColumnDef<T> {
  return generateColumn<T>({
    key,
    header,
    cell: cell ?? (value => <CurrencyCell value={value} />),
    meta: {
      headerClassName: "text-right",
      cellClassName: "justify-self-end"
    }
  });
}
export function CurrencyCell(t0) {
  const $ = _c(5);
  const {
    value,
    className
  } = t0;
  const t1 = Number(value);
  let t2;
  if ($[0] !== className) {
    t2 = cn(className, "justify-self-end w-1/2");
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = <EstimateTablePrice price={t1} size="md" className={t2} />;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}